import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SwUpdate } from '@angular/service-worker';
import { TooltipModule } from 'primeng/tooltip';
import { VersionService } from '../../core/services/version.service';

/**
 * Pie de pagina. Ademas del ano, muestra la version que este navegador tiene
 * cargada, para que al reportar algo se sepa de que version se esta hablando.
 *
 * Dos avisos aparecen solos cuando hacen falta:
 *  - hay version nueva publicada  -> el boton baja la version y recarga
 *  - el API no va a la par        -> se desplego una pieza y la otra no
 *
 * La version nueva ya no recarga las pestanas por su cuenta: el aviso se queda aqui hasta que
 * el usuario termine lo que esta capturando.
 */
@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  template: `
    <footer class="footer">
        <div class="container-fluid">
            <div class="row">
                <div class="col-sm-6">
                    {{ year }} &copy; Belatz.
                    <span class="text-muted ms-2" [pTooltip]="detalle" tooltipPosition="top">{{ version.etiqueta() }}</span>

                    @if (version.hayVersionNueva()) {
                      <button type="button" class="btn btn-link btn-sm p-0 ms-2 align-baseline"
                              [disabled]="actualizando()" (click)="recargar()"
                              pTooltip="Guarda lo que estes capturando antes de recargar." tooltipPosition="top">
                        @if (actualizando()) {
                          Bajando la version {{ version.enServidor()?.numero }}&hellip;
                        } @else {
                          Hay una version nueva ({{ version.enServidor()?.numero }}) &mdash; recargar
                        }
                      </button>
                    }

                    @if (version.desajusteConApi()) {
                      <span class="badge bg-warning text-dark ms-2"
                            pTooltip="La pantalla y el servidor no traen la misma version. Avisa a sistemas."
                            tooltipPosition="top">
                        API v{{ version.api()?.version }}
                      </span>
                    }
                </div>
                <div class="col-sm-6"><div class="text-sm-end d-none d-sm-block">Design by imperha</div></div>
            </div>
        </div>
    </footer>
  `,
  styles: []
})
export class FooterComponent {
  readonly version = inject(VersionService);
  private readonly sw = inject(SwUpdate);
  year = new Date().getFullYear();

  /** Mientras el worker baja la version nueva, antes de recargar. */
  readonly actualizando = signal(false);

  /** Commit y fecha de compilacion, para el tooltip. */
  get detalle(): string {
    const v = this.version.app;
    const commit = v.commit ? ` (${v.commit})` : '';
    return `Compilado ${new Date(v.compilado).toLocaleString('es-MX')}${commit}`;
  }

  /**
   * Primero le pide al worker que baje la version nueva y despues recarga: si se recargara
   * antes, el worker serviria otra vez la version que tiene guardada.
   */
  async recargar(): Promise<void> {
    if (this.actualizando()) return;
    this.actualizando.set(true);
    if (this.sw.isEnabled) {
      try {
        await this.sw.checkForUpdate();
      } catch {
        // Sin red o sin worker: se recarga igual.
      }
    }
    window.location.reload();
  }
}
